import { PureComponent, ReactNode } from 'react';
import countryCodes from 'country-codes-list';
import { Dropdown, Empty, Menu } from 'antd';
import Fuse from 'fuse.js';
import debounce from 'lodash/debounce';
import { crossIcon, downArrowNew } from './Icons';
import { CountryCode, parsePhoneNumber } from 'libphonenumber-js';

export const fuseConfig = {
    keys: ['_code', '_name', '_codeWithSign'],
    threshold: 0.1,
    ignoreLocation: true,
    useExtendedSearch: true,
    shouldSort: false,
};

type Props = {
    onSelect: (code: string, countryCodeEnum: CountryCode) => void;
    value?: string;
    isModal?: boolean;
    disabled?: boolean;
    countryCodeData: countryCodes.CountryData[];
    phoneNumber?: string;
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore

interface CountryData {
    countryCallingCode: string;
    countryCode: string;
    countryNameEn: string;
    flag: string;
}

interface State {
    codes?: CountryData[];
    currSearchValue?: string;
}

class PhoneCountryCode extends PureComponent<Props, State> {
    state: State = {
        codes: undefined,
        currSearchValue: undefined,
    };

    setAllCountryCodes = async (): Promise<void> => {
        const codes = this.props.countryCodeData.map((val) => ({
            countryCallingCode: val['countryCallingCode'],
            countryCode: val['countryCode'],
            countryNameEn: val['countryNameEn'],
            flag: val['flag'],
        }));

        this.setState({
            codes,
            currSearchValue: undefined,
        });

        this.fuse = new Fuse(
            codes.map((c) => {
                return {
                    ...c,
                    _code: c['countryCallingCode'],
                    _name: c['countryNameEn'],
                    _codeWithSign: [`+ ${c['countryCallingCode']}`, `+${c['countryCallingCode']}`],
                };
            }),
            fuseConfig,
        );
    };

    componentDidMount(): void {
        this.setAllCountryCodes();
    }

    onSearch = (): void => {
        const { currSearchValue } = this.state;

        if (currSearchValue) {
            const value = this.fuse.search(currSearchValue);

            this.setState({
                codes: value.length
                    ? value.map((val) => ({
                          countryCallingCode: val.item['countryCallingCode'],
                          countryCode: val.item['countryCode'],
                          countryNameEn: val.item['countryNameEn'],
                          flag: val.item['flag'],
                      }))
                    : [],
                currSearchValue,
            });
        } else {
            this.setAllCountryCodes();
        }
    };

    debouncedSearch = debounce(this.onSearch, 500);

    fuse = new Fuse([] as CountryData[], fuseConfig);

    render(): ReactNode {
        const { value, disabled, isModal } = this.props;
        const { codes, currSearchValue } = this.state;

        return (
            <div
                style={{ width: 80 }}
                className={`cursor-pointer relative flex justify-center items-center px-8 h-full rounded-4 ${
                    isModal ? '' : 'border border-darwin-neutral-400'
                }`}
            >
                {codes ? (
                    <Dropdown
                        destroyPopupOnHide
                        overlay={
                            <Menu className="rounded-4 my-0 py-0">
                                <div className="flex bg-white border-b border-darwin-neutral_new-200 items-center justify-center">
                                    <input
                                        value={currSearchValue}
                                        onChange={(e) => {
                                            this.setState({
                                                currSearchValue: e.target.value ?? undefined,
                                            });

                                            this.debouncedSearch();
                                        }}
                                        placeholder="Search country..."
                                        className="w-90p bg-white py-6 rounded-4"
                                        autoFocus
                                    />
                                </div>
                                <div className="max-h-200 min-w-240 max-w-240 overflow-auto">
                                    {codes?.length ? (
                                        codes.map((code) => (
                                            <Menu.Item
                                                onClick={() => {
                                                    this.props.onSelect(
                                                        `+${code['countryCallingCode']}`,
                                                        code['countryCode'] as CountryCode,
                                                    );
                                                }}
                                                className="py-8"
                                            >
                                                <div
                                                    className="flex gap-8 items-center"
                                                    style={{ whiteSpace: 'break-spaces' }}
                                                >
                                                    <p>
                                                        {code['countryNameEn']} (+
                                                        {code['countryCallingCode']})
                                                    </p>
                                                </div>
                                            </Menu.Item>
                                        ))
                                    ) : (
                                        <Empty
                                            className="my-12 w-240"
                                            description="No countries found"
                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        />
                                    )}
                                </div>
                            </Menu>
                        }
                        trigger={['click']}
                        disabled={disabled}
                        getPopupContainer={(target) => target.parentNode as HTMLElement}
                        placement="bottomLeft"
                        onVisibleChange={(visible) => {
                            if (!visible) {
                                this.setState({ currSearchValue: undefined });
                                this.setAllCountryCodes();
                            }
                        }}
                    >
                        <div
                            className={`w-full flex items-center gap-8 text-neutral-black font-600 focus:outline-none bg-transparent`}
                        >
                            <p>{value}</p>
                            <span className="ml-auto">{downArrowNew('w-8 h-8')}</span>
                        </div>
                    </Dropdown>
                ) : null}
            </div>
        );
    }
}

export default PhoneCountryCode;
